import { useEffect, useMemo, useState } from "react";
import { Lock } from "iconoir-react";
import type { Puzzle, PuzzleSummary } from "../../shared/nonogram/types";
import { fetchPuzzleSummaries, fetchSolvedPuzzles } from "../lib/api";
import { track } from "../lib/analytics";
import { loadStats } from "../lib/storage";
import { SeoContent } from "../components/SeoContent";

type Tier = {
  key: string;
  size: Puzzle["size"];
  label: string;
  blurb: string;
  holds: (summary: PuzzleSummary) => boolean;
};

const isGlyph = (summary: PuzzleSummary) => summary.theme === "glyphs";

/**
 * The alphabet sits at the end rather than inside Pocket. It is a complete set with
 * its own kind of satisfaction, and leading the page with 37 locked letters buries
 * the pictures the collection is actually about.
 */
const TIERS: Tier[] = [
  { key: "pocket", size: 5, label: "Pocket", blurb: "5×5 · symbols you read in one glance", holds: (s) => s.size === 5 && !isGlyph(s) },
  { key: "studio", size: 10, label: "Studio", blurb: "10×10 · the everyday size", holds: (s) => s.size === 10 },
  { key: "gallery", size: 15, label: "Gallery", blurb: "15×15 · weekend pictures with room for detail", holds: (s) => s.size === 15 },
  { key: "grand", size: 20, label: "Grand", blurb: "20×20 · the long sittings, edges and interiors", holds: (s) => s.size === 20 },
  { key: "epic", size: 25, label: "Epic", blurb: "25×25 · a whole evening in one grid", holds: (s) => s.size === 25 },
  { key: "alphabet", size: 5, label: "Alphabet", blurb: "5×5 · the full A–Z and 0–9 set", holds: isGlyph },
];

const PLAY_HREF: Record<Puzzle["size"], string> = {
  5: "/nonogram/easy",
  10: "/nonogram",
  15: "/nonogram/hard",
  20: "/nonogram/20x20",
  25: "/nonogram/25x25",
};

/**
 * A solved picture, drawn small. Filled squares only — the empty ones stay the page
 * background so a wall of these reads as a set of pictures rather than a wall of grids.
 */
function Thumbnail({ puzzle }: { puzzle: Puzzle }) {
  const side = puzzle.size;
  return (
    <svg
      className="collection-art"
      viewBox={`0 0 ${side} ${side}`}
      role="img"
      aria-label={`${puzzle.title}, a solved ${side}×${side} picture`}
    >
      {puzzle.solution.map((cell, index) =>
        cell ? <rect key={index} x={index % side} y={Math.floor(index / side)} width="1" height="1" /> : null,
      )}
    </svg>
  );
}

export function Collection() {
  const [summaries, setSummaries] = useState<PuzzleSummary[]>();
  const [solved, setSolved] = useState<Map<string, Puzzle>>(new Map());
  const [error, setError] = useState<string>();
  const [filterChoice, setFilterChoice] = useState<boolean>();

  useEffect(() => {
    const controller = new AbortController();
    const solvedIds = Object.keys(loadStats().bestTimes);

    Promise.all([
      fetchPuzzleSummaries(controller.signal),
      fetchSolvedPuzzles(solvedIds, controller.signal),
    ])
      .then(([all, found]) => {
        setSummaries(all.puzzles);
        setSolved(new Map(found.puzzles.map((puzzle) => [puzzle.id, puzzle])));
        track("collection_view", { found: found.puzzles.length, total: all.puzzles.length });
      })
      .catch((cause: unknown) => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(cause instanceof Error ? cause.message : "The collection could not be loaded.");
      });

    return () => controller.abort();
  }, []);

  const tiers = useMemo(
    () =>
      TIERS.map((tier) => {
        const items = (summaries ?? []).filter(tier.holds);
        return { ...tier, items, found: items.filter((item) => solved.has(item.id)).length };
      }),
    [summaries, solved],
  );

  const total = summaries?.length ?? 0;
  const found = solved.size;
  // Once there is something to look at, the collection is a record of what you found,
  // so it opens on that. A player with nothing yet sees the whole locked set instead —
  // filtering an empty collection down to nothing is not a view of anything.
  const onlyFound = filterChoice ?? found > 0;
  const percent = total === 0 ? 0 : Math.round((found / total) * 100);

  return (
    <main className="prose-page collection-page" id="main-content">
      <p className="eyebrow">Your picture collection</p>
      <h1>Collection</h1>
      <p className="lede">
        Every puzzle hides a picture. Solve one and it is added here — with its name — so the set fills in as you play.
      </p>

      {error && <p role="alert">{error} Please refresh and try again.</p>}

      {summaries === undefined && !error && (
        <p className="collection-loading" aria-busy="true">Opening the collection…</p>
      )}

      {summaries !== undefined && (
        <>
          <section className="collection-progress" aria-label="Collection progress">
            <div className="collection-count">
              <strong>{found}</strong>
              <span>of {total} pictures found</span>
            </div>
            <div
              className="collection-bar"
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${percent}% of the collection found`}
            >
              <i style={{ width: `${percent}%` }} />
            </div>
            {found === 0 ? (
              <p className="collection-hint">
                Nothing here yet. <a href="/">Solve today’s puzzle</a> to open the first frame.
              </p>
            ) : (
              <label className="collection-filter">
                <input type="checkbox" checked={onlyFound} onChange={(event) => setFilterChoice(event.target.checked)} />
                Show only what I have found
              </label>
            )}
          </section>

          {tiers.map((tier) => {
            const visible = onlyFound ? tier.items.filter((item) => solved.has(item.id)) : tier.items;
            if (visible.length === 0) return null;
            return (
              <section className="collection-tier" key={tier.key} aria-label={`${tier.label} pictures`}>
                <div className="collection-tier-head">
                  <h2>
                    {tier.label} <small>{tier.blurb}</small>
                  </h2>
                  <p>
                    {tier.found} / {tier.items.length}
                    <a href={PLAY_HREF[tier.size]}>Play {tier.size}×{tier.size} <span aria-hidden="true">→</span></a>
                  </p>
                </div>

                <ul className="collection-grid" data-size={tier.size}>
                  {visible.map((summary) => {
                    const puzzle = solved.get(summary.id);
                    if (!puzzle) {
                      return (
                        // A locked frame carries no words. Repeating "not found yet" three
                        // hundred times reads as noise, and the label is the one thing that
                        // could leak: even the theme narrows 10×10 to a guess worth making.
                        <li className="collection-cell is-locked" key={summary.id}>
                          <span className="collection-lock" role="img" aria-label="Not found yet">
                            <Lock width={15} height={15} strokeWidth={2} aria-hidden="true" />
                          </span>
                        </li>
                      );
                    }
                    return (
                      <li className="collection-cell is-found" key={summary.id}>
                        <Thumbnail puzzle={puzzle} />
                        <span className="collection-label">
                          <b>
                            <span aria-hidden="true">{puzzle.emoji}</span> {puzzle.title}
                          </b>
                          <small>{puzzle.theme}</small>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </>
      )}

      <SeoContent path="/collection" />
    </main>
  );
}
